import { createContext, useContext, useCallback, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useGlobalScanner } from 'hooks/useGlobalScanner'
import { parseQrType } from 'utils/parseQrType'
import {
  setScanResult,
  setScanStatus,
  clearScan,
  setShowDetailModal,
  setParsedDetail,
} from 'features/private-views/scanner/scannerSlice'
import ScanDetailModal from 'features/private-views/scanner/ScanDetailModal'
import { handleScanByContext } from 'features/private-views/scanner/scannerHandlers'
import LoadingAnimation from 'components/LoadingAnimation'
import { URL_SCANNER_SETTING } from 'utils/pathUrl'

const ScannerContext = createContext({
  lastScan: null,
  scanStatus: 'idle',
  clearLastScan: () => {},
})

export const useScannerContext = () => useContext(ScannerContext)

export const ScannerProvider = ({ children }) => {
  const dispatch = useDispatch()
  const store = useStore()
  const location = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { lastScan, scanStatus, showDetailModal, parsedDetail } = useSelector(
    (s) => s.scanner,
  )
  const pendingCommandRef = useRef(null)
  const fragmentBufferRef = useRef('')
  const fragmentTimeoutRef = useRef(null)

  /**
   * Core logic to process a finalized (or single-burst) scan string.
   */
  const handleScanActual = useCallback(
    (normalizedValue) => {
      // Show global loading overlay immediately when scan is detected
      dispatch(setScanStatus({ status: 'processing' }))

      const now = Date.now()
      let scanResult = parseQrType(normalizedValue)
      const isItemsFragment = normalizedValue.startsWith('items=')
      const hasItemsPart = /(^|\|)items=/.test(normalizedValue)
      const hasInlineItems =
        Array.isArray(scanResult?.items) && scanResult.items.length > 0

      const parseLooseItems = (raw) => {
        if (!raw) return []
        const tryDecode = (v) => {
          try {
            return decodeURIComponent(v)
          } catch (e) {
            return v
          }
        }
        const source = tryDecode(String(raw).trim())
        const chunks = source.match(/\{[^{}]*\}/g) || [source]
        const parsed = []
        for (const chunk of chunks) {
          const obj = {}
          const pairRegex =
            /"?([a-zA-Z0-9_\?]+)"?\s*:\s*("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|[^,\}\]]+)/g
          let m
          while ((m = pairRegex.exec(chunk)) !== null) {
            const key = m[1]
            const token = String(m[2] || '').trim()
            let val
            if (
              (token.startsWith('"') && token.endsWith('"')) ||
              (token.startsWith("'") && token.endsWith("'"))
            ) {
              val = token.slice(1, -1)
            } else if (token === 'null') {
              val = null
            } else if (token === 'true' || token === 'false') {
              val = token === 'true'
            } else if (/^-?\d+(\.\d+)?$/.test(token)) {
              val = Number(token)
            } else {
              val = token
            }
            obj[key] = val
          }
          if (Object.keys(obj).length > 0) parsed.push(obj)
        }
        return parsed
      }

      const extractSingleSellItem = (raw) => {
        const src = String(raw || '')
        const getNum = (k) => {
          const m = src.match(
            new RegExp(
              `(?:"|')?${k}(?:"|')?\\s*[:=]\\s*(?:"|')?([0-9]+(?:\\.[0-9]+)?)`,
              'i',
            ),
          )
          return m?.[1] ?? null
        }
        const getStr = (k) => {
          const m = src.match(
            new RegExp(
              `(?:"|')?${k}(?:"|')?\\s*[:=]\\s*(?:"([^"]+)"|'([^']+)')`,
              'i',
            ),
          )
          return m?.[1] || m?.[2] || null
        }
        const invId = getNum('inventoryId')
        const itemId = getNum('itemId')
        const amount = getNum('amount')
        const name = getStr('nameItem')
        const sku = getStr('SKU') || getStr('sku')
        if (!invId && !itemId && !name) return null
        return {
          ...(itemId ? { itemId: Number(itemId) } : {}),
          ...(invId ? { inventoryId: Number(invId) } : {}),
          ...(amount ? { amount: Number(amount) } : {}),
          ...(name ? { nameItem: name } : {}),
          ...(sku ? { SKU: sku } : {}),
        }
      }

      if (scanResult.type !== 'command') {
        const m = normalizedValue.match(/(?:^|\|)format=([^|\s]+)/)
        if (m?.[1])
          scanResult = {
            type: 'command',
            command: m[1],
            items: [],
            value: normalizedValue,
          }
      }

      if (
        scanResult.type !== 'command' &&
        isItemsFragment &&
        pendingCommandRef.current?.command
      ) {
        scanResult = parseQrType(
          `format=${pendingCommandRef.current.command}|${normalizedValue}`,
        )
        if (scanResult.type === 'command') pendingCommandRef.current = null
      }

      if (
        isItemsFragment &&
        scanResult.type !== 'command' &&
        !pendingCommandRef.current?.command
      ) {
        enqueueSnackbar(
          'Potongan data item QR diterima tanpa format command.',
          { variant: 'warning' },
        )
        dispatch(setScanStatus({ status: 'idle' }))
        return
      }

      if (
        scanResult.type === 'command' &&
        scanResult.command &&
        !hasItemsPart &&
        !hasInlineItems
      ) {
        pendingCommandRef.current = {
          command: scanResult.command,
          timestamp: now,
        }
        enqueueSnackbar('Potongan QR diterima, menunggu data item...', {
          variant: 'info',
        })
        dispatch(setScanStatus({ status: 'idle' }))
        return
      }

      if (scanResult.type === 'unknown') {
        enqueueSnackbar('Format QR tidak dikenali', { variant: 'warning' })
        dispatch(setScanStatus({ status: 'idle' }))
        return
      }

      if (scanResult.type === 'command') {
        let items = Array.isArray(scanResult.items) ? scanResult.items : []
        if (items.length === 0) {
          const idx = normalizedValue.indexOf('items=')
          if (idx !== -1) {
            const raw = normalizedValue.substring(idx + 6)
            try {
              items = JSON.parse(raw)
              if (!Array.isArray(items)) items = [items]
            } catch (e) {
              try {
                const fixed = raw
                  .replace(/([\{\,]\s*)([a-zA-Z0-9_\?]+)\s*\:/g, '$1"$2":')
                  .replace(/:\s*'([^']*)'/g, ':"$1"')
                items = JSON.parse(fixed)
                if (!Array.isArray(items)) items = [items]
              } catch (err) {
                items = parseLooseItems(raw)
              }
            }
          }
        }
        if (items.length === 0) {
          const idx = normalizedValue.indexOf('items=')
          items = parseLooseItems(
            idx !== -1 ? normalizedValue.substring(idx + 6) : normalizedValue,
          )
        }
        if (
          items.length === 0 &&
          scanResult.command === 'createSellTransaction'
        ) {
          const single = extractSingleSellItem(normalizedValue)
          if (single) items = [single]
        }
        if (items.length === 0) {
          enqueueSnackbar(
            'Data item pada QR tidak terbaca. Silakan scan ulang.',
            { variant: 'warning' },
          )
          pendingCommandRef.current = null
          dispatch(setScanStatus({ status: 'idle' }))
          return
        }

        dispatch(
          setParsedDetail({
            ...scanResult,
            items,
            value: normalizedValue,
          }),
        )
        dispatch(setShowDetailModal(true))

        pendingCommandRef.current = null
        dispatch(setScanStatus({ status: 'processing' }))
        if (scanResult.command === 'createSellTransaction') {
          const normText = (v) =>
            v == null
              ? ''
              : String(v)
                  .trim()
                  .replace(/^['"]+|['"]+$/g, '')
                  .replace(/\\+/g, '')
          const toNum = (v) => {
            if (v == null || v === '') return null
            if (typeof v === 'number' && Number.isFinite(v)) return v
            const raw = String(v).trim()
            const d = Number(raw)
            if (!Number.isNaN(d)) return d
            const m = raw.match(/-?\d+(?:\.\d+)?/)
            return m ? Number(m[0]) : null
          }
          const prefillOrders = {
            item: items.map((it, idx) => {
              const invIdRaw =
                it?.inventoryId ??
                it?.inventory?.id ??
                it?.gudang ??
                it?.['gudang?'] ??
                null
              const invId = toNum(invIdRaw) ?? normText(invIdRaw)
              const grpIdRaw = it?.groupId ?? it?.group?.id ?? null
              const grpId = toNum(grpIdRaw) ?? normText(grpIdRaw)
              const itIdRaw = it?.itemId ?? it?.id ?? null
              const itId = toNum(itIdRaw) ?? normText(itIdRaw)
              const amtRaw = it?.amount ?? ''
              const amt = toNum(amtRaw) ?? amtRaw
              const name = normText(it?.nameItem)
              const sku = normText(it?.SKU || it?.sku)
              const isInv =
                invId !== null && invId !== undefined && invId !== ''
              return {
                orderItemId: idx + 1,
                ...(itId !== null && itId !== undefined && itId !== ''
                  ? { itemId: itId }
                  : {}),
                ...(!isInv && name ? { nameItem: name } : {}),
                ...(it?.amount !== undefined ? { amount: amt } : {}),
                ...(sku ? { SKU: sku } : {}),
                ...(isInv
                  ? {
                      inventoryId: invId,
                      itemDetail: {
                        inventoryId: invId,
                        ...(grpId ? { groupId: grpId } : {}),
                      },
                      ...(grpId ? { groupId: grpId } : {}),
                      originalAmount: amt,
                      isFromManageStock: true,
                    }
                  : {}),
              }
            }),
            type: 'SELL',
          }
          navigate('/sell-commodity', {
            state: {
              prefillOrders,
              prefillAddItemCount: items.map((_, i) => i + 1),
              prefillSource: 'scanner',
            },
          })
          enqueueSnackbar('Memulai transaksi penjualan...', {
            variant: 'success',
          })
        } else if (scanResult.command === 'createInventory') {
          navigate('/inventory', {
            state: { action: 'createInventory', qrItems: items },
          })
          enqueueSnackbar('Membuka inventori...', { variant: 'success' })
        } else if (scanResult.command === 'searchInventory') {
          const searchItem = items[0]
          navigate(
            searchItem?.groupId
              ? `/inventory/${searchItem.groupId}`
              : '/inventory',
            {
              state: {
                action: 'searchInventory',
                query: searchItem?.name || searchItem?.id || '',
              },
            },
          )
          enqueueSnackbar('Mencari inventori...', { variant: 'success' })
        } else {
          enqueueSnackbar(`Perintah ${scanResult.command} tidak dikenal`, {
            variant: 'warning',
          })
        }
        setTimeout(() => {
          dispatch(clearScan())
        }, 1500)
        return
      }

      dispatch(setScanResult(scanResult))
      if (scanResult.type === 'PM-Inventory') {
        const currentDetail = store.getState().scanner.parsedDetail
        const isFollowUp =
          currentDetail &&
          ((scanResult.serial && currentDetail.serial === scanResult.serial) ||
            (scanResult.rid && currentDetail.rid === scanResult.rid) ||
            (!scanResult.serial &&
              !scanResult.batch &&
              (scanResult.rid || scanResult.productDescription)))
        if (isFollowUp) {
          const fullRaw = currentDetail.value.includes(scanResult.value)
            ? currentDetail.value
            : `${currentDetail.value}${scanResult.value}`

          // Re-parse the full combined string to ensure fields split across bursts (like Batch) are captured
          const reParsed = parseQrType(fullRaw)
          const merged = {
            ...currentDetail,
            ...reParsed,
            value: fullRaw,
          }
          dispatch(setParsedDetail(merged))
        } else {
          dispatch(setParsedDetail(scanResult))
        }
        dispatch(setShowDetailModal(true))
      }
      // Run the context-specific handler, then clear loading
      Promise.resolve(
        handleScanByContext({
          scanResult,
          dispatch,
          getState: store.getState,
          enqueueSnackbar,
          pathname: location.pathname,
        }),
      ).finally(() => {
        // Auto-clear loading after handler completes (with small delay for UX)
        setTimeout(() => {
          const currentStatus = store.getState().scanner.scanStatus
          if (currentStatus === 'processing') {
            dispatch(setScanStatus({ status: 'idle' }))
          }
        }, 800)
      })
    },
    [dispatch, store, location.pathname, enqueueSnackbar, navigate],
  )

  /**
   * Main entry point for raw scan bursts.
   * Implements fragment accumulation for long PM-Inventory QRs.
   */
  const handleScan = useCallback(
    (scannedValue) => {
      const trimmedValue = String(scannedValue || '').trim()
      const normalizedValue = (() => {
        let val = trimmedValue
        if (val.startsWith('ormat=')) val = `f${val}`
        if (val.startsWith('tems=')) val = `i${val}`
        return val
      })()
      const now = Date.now()

      if (
        pendingCommandRef.current &&
        now - pendingCommandRef.current.timestamp > 4000
      ) {
        pendingCommandRef.current = null
      }

      // Total Accumulation Logic: Glue EVERYTHING that arrives in quick succession.
      if (normalizedValue) {
        // Show loading overlay immediately when ANY scan burst is detected
        dispatch(setScanStatus({ status: 'processing' }))

        if (fragmentTimeoutRef.current) {
          clearTimeout(fragmentTimeoutRef.current)
          fragmentTimeoutRef.current = null
        }

        // If this is a fresh start (starts with (01)), and we don't have an active buffer, or it's been a while, reset.
        const isFreshStart =
          normalizedValue.startsWith('(01)') &&
          (!fragmentBufferRef.current ||
            fragmentBufferRef.current.includes('|P=') ||
            fragmentBufferRef.current.includes('|CHK='))

        if (isFreshStart) {
          fragmentBufferRef.current = normalizedValue
        } else {
          // Only append if it's not already a duplicate of what we just got
          if (!fragmentBufferRef.current.endsWith(normalizedValue)) {
            fragmentBufferRef.current += normalizedValue
          }
        }

        const fullBuffer = fragmentBufferRef.current
        const isComplete =
          fullBuffer.includes('|P=') ||
          fullBuffer.includes('|PMD=') ||
          fullBuffer.includes('|CHK=')

        if (isComplete) {
          const finalValue = fragmentBufferRef.current
          fragmentBufferRef.current = ''
          handleScanActual(finalValue)
        } else {
          enqueueSnackbar('Menerima potongan data...', {
            variant: 'info',
            autoHideDuration: 1500,
            key: 'qr_fragment_info',
          })
          fragmentTimeoutRef.current = setTimeout(() => {
            const staleBuffer = fragmentBufferRef.current

            // If we time out, clear the global loading too
            dispatch(setScanStatus({ status: 'idle' }))

            if (staleBuffer) handleScanActual(staleBuffer)
          }, 30000)
        }
        return
      }

      handleScanActual(normalizedValue)
    },
    [enqueueSnackbar, handleScanActual],
  )

  const { isScanning } = useGlobalScanner(handleScan, {
    enabled: location.pathname !== URL_SCANNER_SETTING,
    isGlobal: true,
  })

  const clearLastScan = useCallback(() => {
    dispatch(clearScan())
  }, [dispatch])

  return (
    <ScannerContext.Provider value={{ lastScan, scanStatus, clearLastScan }}>
      {(scanStatus === 'processing' || isScanning) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}
        >
          <LoadingAnimation color="primary" />
          <h5 className="mt-3 fw-semibold text-primary">Memproses QR...</h5>
        </div>
      )}
      <ScanDetailModal
        isOpen={showDetailModal}
        toggle={() => dispatch(setShowDetailModal(!showDetailModal))}
        data={parsedDetail}
      />
      {children}
    </ScannerContext.Provider>
  )
}

export default ScannerProvider
